import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { pageview, event } from "../analytics/analytics";
import { getConfiguredBillingPlans, getDefaultPlanId, resolvePlanId, mergePlanWithStatic } from "../logic/planSelection";
import { PRICING_COPY } from "../config/pricing";
import { getStoredRoleHomePath } from "../logic/homeRoute";
import { Card, Badge } from "../Components/ui";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "../Shared/contact";
import SampleQuestion from "../Components/Landing/SampleQuestion";
import { FOUNDER_STORY, PRODUCT_FEATURES, FAMILY_QUOTES, NEXT_SELECTIVE_TEST } from "../config/landingProof";

const PRIMARY_CTA_LABEL = 'Start Free 7-Day Trial';
const SUBJECT_FOCUS_COPY = 'TestCoachAI covers all four NSW Selective High School Placement Test components — Reading, Mathematical Reasoning, Thinking Skills, and Writing.';
const PRACTICE_REVISION_COPY = 'Daily Practice and Practice Mistakes are available across all four components, with AI-marked feedback for Writing responses.';
const EXAM_MODE_COPY = 'Full-length, timed exam-style sessions are available for all four components.';

const trustItems = ['7-day free trial', 'No card required', 'Cancel anytime', 'Choose monthly or yearly'];

const workflowSteps = [
  {
    step: '1',
    title: 'Create your parent account',
    body: 'Register in a few minutes and activate the free trial without entering payment details.'
  },
  {
    step: '2',
    title: 'Add your child',
    body: 'Set up your child on the next screen so practice can start straight away.'
  },
  {
    step: '3',
    title: 'Start Daily Practice',
    body: 'Students begin with short daily sessions that keep practice manageable and consistent.'
  },
  {
    step: '4',
    title: 'Track progress clearly',
    body: 'Parents can review scores, timing trends, and weak areas from the dashboard.'
  }
];

const featureCards = [
  {
    title: 'Daily Practice',
    body: 'Fresh question sets each session, with instant feedback and step-by-step explanations, help students build confidence and keep momentum without long cram sessions.'
  },
  {
    title: 'Practice Mistakes',
    body: 'Once a student has built up some practice history, Practice Mistakes brings back exactly the question types and weak areas they got wrong, so follow-up work stays targeted.'
  },
  {
    title: 'AI-Marked Writing',
    body: 'Writing responses are marked against a 25-point rubric and given a band, from Exceptional to Underdeveloped, with a detailed feedback report covering strengths and weaknesses — not just a single score.'
  },
  {
    title: 'Full-Length Exam Mode',
    body: 'Timed, full-length exam-style sessions for all four components help students practise pacing and real test conditions.'
  },
  {
    title: 'Parent Dashboard',
    body: 'Parents can see score trends, timing patterns, and question areas that need more attention.'
  }
];

const proofCards = [
  {
    title: 'Instant answer feedback',
    body: 'Practice sessions tell students what happened on each question while the attempt is still fresh.'
  },
  {
    title: 'Practice Mistakes, not just more of the same',
    body: 'Once your child has built up a bit of practice history, follow-up questions focus on exactly what they got wrong, so revision stays targeted instead of starting over.'
  },
  {
    title: 'Progress visibility for parents',
    body: 'Parents can review completed tests, performance trends, and timing summaries from their dashboard.'
  },
  {
    title: 'Low-risk start',
    body: 'Families can register, add a child, and start the free trial before deciding whether to continue.'
  }
];

const audiencePoints = [
  'NSW parents supporting Year 4-6 selective test preparation',
  'Families who want a steady, manageable daily practice habit for their child',
  'Parents who want visibility into progress instead of guesswork'
];

const differentiators = [
  {
    title: 'Daily, not weekly',
    body: 'Short practice sessions every day build consistency alongside — or instead of — a weekly tutoring slot.'
  },
  {
    title: 'Every answer explained',
    body: "Step-by-step explanations help your child understand why an answer is right, not just whether it was, before moving on."
  },
  {
    title: 'Writing feedback the same session',
    body: 'AI marking gives a band, score, and a strengths-and-weaknesses report right away, instead of waiting for marked work to come back.'
  },
  {
    title: 'A dashboard you can check anytime',
    body: "See your child's progress, timing, and weak areas whenever you check in — no need to wait for a scheduled update."
  }
];

const faqEntries = [
  {
    question: 'How much time should my child spend each day?',
    answer: 'Many families start with 5 to 10 minutes of focused daily practice and build up from there. For a test this competitive, consistency over weeks matters more than occasional long sessions right before test day.'
  },
  {
    question: 'What happens after I register?',
    answer: 'You create the parent account first, add your child on the next screen, and then your child can begin practice immediately.'
  },
  {
    question: 'Do I need a credit card to start?',
    answer: 'No. The free 7-day trial starts without entering payment details.'
  },
  {
    question: 'How much does my child need to know before starting?',
    answer: "Nothing extra — children can start fresh. Step-by-step AI explanations help them understand each problem and concept as they go, and practising multiple questions of the same pattern reinforces what they've just learned."
  },
  {
    question: 'Which subjects are supported right now?',
    answer: `${SUBJECT_FOCUS_COPY} ${PRACTICE_REVISION_COPY} ${EXAM_MODE_COPY}`
  },
  {
    question: "Can I see my child's progress?",
    answer: 'Yes. Parents can review completed tests, score trends, timing patterns, and weak-question areas from the dashboard.'
  },
  {
    question: "Is my child's data safe?",
    answer: (
      <>
        Yes. We only collect what's needed to run practice sessions and your parent dashboard. Writing responses sent for AI marking
        have identifying details removed before processing and are deleted within 90 days. You can view, update, or delete your
        child's data at any time — see our <Link to="/privacy" className="underline">Privacy Policy</Link> for the full details.
      </>
    )
  },
  {
    question: 'What happens after the trial?',
    answer: `${PRICING_COPY} Monthly access renews until cancelled, while the 12-month option is one upfront payment that ends automatically after the term.`
  }
];

const DAY_MS = 24 * 60 * 60 * 1000;

// STUD-899: the closing CTA names the next NSW selective test date. Returns null once the
// configured date has passed, so the page falls back to undated copy instead of a stale date.
export function getTestDateCta(now = new Date()) {
  const testDate = new Date(`${NEXT_SELECTIVE_TEST.testDate}T00:00:00+10:00`);
  if (Number.isNaN(testDate.getTime()) || now >= testDate) return null;
  const weeks = Math.floor((testDate - now) / (7 * DAY_MS));
  const opens = new Date(`${NEXT_SELECTIVE_TEST.applicationsOpenDate}T00:00:00+10:00`);
  const closes = new Date(`${NEXT_SELECTIVE_TEST.applicationsCloseDate}T23:59:59+11:00`);
  let applications = null;
  if (now < opens) {
    applications = `Applications open 5 November 2026 and close ${NEXT_SELECTIVE_TEST.applicationsCloseLabel}.`;
  } else if (now <= closes) {
    applications = `Applications are open until ${NEXT_SELECTIVE_TEST.applicationsCloseLabel}.`;
  }
  return { label: NEXT_SELECTIVE_TEST.label, weeks, applications, sourceUrl: NEXT_SELECTIVE_TEST.sourceUrl };
}

function trackLandingCta(action, label) {
  event({
    category: 'Landing Page',
    action,
    label
  });
}

// Merge API course plan data with static catalog entries so we keep rich copy (features,
// descriptions) while letting a course control which plans it offers and their Chargebee ids.
// The advertised price and renewal terms always come from the local catalog — see
// mergePlanWithStatic.
function mergePlansWithStatic(apiPlans, staticPlans) {
  if (!Array.isArray(apiPlans) || apiPlans.length === 0) return staticPlans;
  const byKey = {};
  staticPlans.forEach(p => { byKey[p.key] = p; });
  return apiPlans
    .filter(p => p.key && p.chargebeeItemPriceId)
    .map(apiPlan => mergePlanWithStatic(byKey[apiPlan.key], apiPlan));
}

function getCoursePlans(course, staticPlans) {
  const merged = mergePlansWithStatic(course.plans || [], staticPlans);
  return merged.length > 0 ? merged : staticPlans;
}

export default function LandingPage() {
  // Redirect authenticated users away from the landing page.
  const token = localStorage.getItem("studyapp.com");
  const defaultPlanId = getDefaultPlanId();
  const planId = resolvePlanId(defaultPlanId);
  const staticPlans = getConfiguredBillingPlans();

  // Courses fetched from the API — each active course gets its own card on the landing page.
  const [availableCourses, setAvailableCourses] = useState([]);

  useEffect(() => {
    pageview("/landing");
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}courses/available`)
      .then(res => res.ok ? res.json() : [])
      .then(courses => {
        if (!Array.isArray(courses) || courses.length === 0) return;
        setAvailableCourses(courses);
      })
      .catch(() => {});
  }, []);

  // Derive default course for hero CTAs (isDefault flag, or first in list).
  const defaultCourse = availableCourses.find(c => c.isDefault) || availableCourses[0] || null;
  const defaultCourseId = defaultCourse?._id || '';

  const registerHref = defaultCourseId
    ? `/register-parent?plan=${encodeURIComponent(planId)}&courseId=${encodeURIComponent(defaultCourseId)}`
    : `/register-parent?plan=${encodeURIComponent(planId)}`;

  const testDateCta = getTestDateCta();

  const heroPlans = mergePlansWithStatic(defaultCourse?.plans || [], staticPlans).slice(0, 2);

  if (token) {
    // Role-aware: a signed-in student belongs on Take Tests, not the parent dashboard.
    return <Navigate to={getStoredRoleHomePath()} replace />;
  }

  return (
    <div className="min-h-screen bg-[var(--bg-page)] text-[var(--text-primary)]">
      <section className="px-6 py-8 md:px-12 md:py-12">
        {/* Three cells, not two columns: on a phone they stack hero → sample question → coverage (so the
            question lands in the first two screens); from lg the question spans both rows on the right
            and coverage sits under the hero on the left, so the two columns end near the same height. */}
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:grid-rows-[auto_1fr] lg:items-start">
          <div className="lg:col-start-1 lg:row-start-1">
            <div className="inline-flex rounded-full border border-[var(--border-accent)] bg-[var(--color-accent-subtle)] px-4 py-2 text-sm font-semibold text-[var(--sage-700)] shadow-[var(--shadow-sm)]">
              Built for NSW parents guiding steady selective test preparation
            </div>
            <h1 className="font-heading mt-6 max-w-3xl text-4xl font-bold leading-tight text-[var(--text-primary)] md:text-5xl">
              Help your child practise consistently for the NSW selective test.
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-[var(--text-secondary)]">
              TestCoachAI gives families a simple way to build daily practice habits, review weaker areas, and track progress clearly from a parent dashboard.
            </p>
            <div className="mt-8 flex flex-col gap-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap">
                {heroPlans.map((plan, index) => {
                  const href = defaultCourseId
                    ? `/register-parent?plan=${encodeURIComponent(plan.key)}&courseId=${encodeURIComponent(defaultCourseId)}`
                    : `/register-parent?plan=${encodeURIComponent(plan.key)}`;
                  return (
                    <Link
                      key={plan.key}
                      to={href}
                      className={`tc-btn tc-btn--lg ${index === 0 ? 'tc-btn--accent' : 'tc-btn--secondary'}`}
                      onClick={() => trackLandingCta('Hero Plan Click', plan.key)}
                    >
                      {PRIMARY_CTA_LABEL} ({plan.priceLabel})
                    </Link>
                  );
                })}
                {!heroPlans.length && (
                  <Link
                    to={registerHref}
                    className="tc-btn tc-btn--accent tc-btn--lg"
                    onClick={() => trackLandingCta('Primary CTA Click', 'Hero')}
                  >
                    {PRIMARY_CTA_LABEL}
                  </Link>
                )}
              </div>
              <a
                href="#how-it-works"
                className="tc-btn tc-btn--secondary tc-btn--lg"
                onClick={() => trackLandingCta('Secondary CTA Click', 'Hero How It Works')}
              >
                See How It Works
              </a>
              <p className="mb-0 text-sm text-[var(--text-muted)]">
                Start the same free trial first, then continue with the plan you selected.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {trustItems.map((item) => (
                <Badge key={item} variant="primary" size="lg" className="w-full justify-center py-3">
                  {item}
                </Badge>
              ))}
            </div>
          </div>
          <div className="relative lg:col-start-2 lg:row-span-2 lg:row-start-1">
            <div className="absolute inset-x-6 top-4 h-72 rounded-full bg-[var(--color-accent-muted)] blur-3xl" aria-hidden="true"></div>
            <div className="relative">
              <SampleQuestion />
            </div>
          </div>
          <Card className="relative lg:col-start-1 lg:row-start-2">
              <h2 className="font-heading text-lg font-semibold text-[var(--text-primary)]">Subject coverage</h2>
              <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)]">{SUBJECT_FOCUS_COPY}</p>
              <p className="mt-2 text-sm leading-7 text-[var(--text-secondary)]">{PRACTICE_REVISION_COPY}</p>
              <p className="mt-2 text-sm leading-7 text-[var(--text-secondary)]">{EXAM_MODE_COPY}</p>
          </Card>
        </div>
      </section>

      <section aria-labelledby="founder-heading" className="px-6 py-8 md:px-12 md:py-12">
        <div className="mx-auto max-w-4xl rounded-2xl bg-[var(--surface-card)] p-5 text-left shadow-[var(--shadow-sm)] ring-1 ring-[var(--border-default)] md:p-10">
          <div className="text-sm font-semibold uppercase tracking-widest text-[var(--brass-600)]">A note from the founder</div>
          <h2 id="founder-heading" className="font-heading mt-3 text-3xl font-bold text-[var(--text-primary)]">{FOUNDER_STORY.title}</h2>
          <div className="mt-5 space-y-4 text-base leading-7 text-[var(--text-secondary)]">
            {FOUNDER_STORY.leadParagraphs.map((para) => <p key={para}>{para}</p>)}
          </div>
          <details className="group mt-4" onToggle={(e) => e.currentTarget.open && trackLandingCta('Founder Story Expand', 'Founder')}>
            <summary className="cursor-pointer font-semibold text-[var(--color-primary)] underline">Read the full story</summary>
            <div className="mt-4 space-y-4 text-base leading-7 text-[var(--text-secondary)]">
              {FOUNDER_STORY.moreParagraphs.map((para) => <p key={para}>{para}</p>)}
            </div>
          </details>
          <p className="font-heading mt-6 text-2xl italic text-[var(--text-primary)]">{FOUNDER_STORY.pullQuote}</p>
          <div className="mt-6 flex items-center gap-3">
            {/* No founder photo yet (owner's call, 2 Nov 2026) — an initial stands in until one is supplied. */}
            <div aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--bg-inverse)] font-heading text-xl font-semibold text-[var(--text-inverse)]">
              {FOUNDER_STORY.signature.charAt(0)}
            </div>
            <div>
              <div className="font-semibold text-[var(--text-primary)]">{FOUNDER_STORY.signature}</div>
              <div className="text-sm text-[var(--text-muted)]">{FOUNDER_STORY.role}</div>
            </div>
          </div>
        </div>
      </section>

      <section aria-labelledby="product-heading" className="px-6 py-8 md:px-12 md:py-12">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-2xl text-center">
            <h2 id="product-heading" className="font-heading text-3xl font-bold text-[var(--text-primary)]">See the real app</h2>
            <p className="mt-4 text-base leading-7 text-[var(--text-secondary)]">
              Screenshots from TestCoachAI as it is today, taken with a test account.
            </p>
          </div>
          {/* One row per feature, image and text alternating sides from md. Images show whole, at
              their own shape: phone captures are capped in width so they never tower. */}
          <div className="mt-10 space-y-12 md:space-y-16">
            {PRODUCT_FEATURES.map((feature, index) => (
              <figure key={feature.title} className="m-0 grid items-center gap-6 md:grid-cols-2 md:gap-12">
                <div className={`flex flex-col items-center gap-3 ${index % 2 ? 'md:order-2' : ''}`}>
                  {feature.images.map((shot) => (
                    <img
                      key={shot.src}
                      src={shot.src}
                      alt={shot.alt}
                      width={shot.width}
                      height={shot.height}
                      loading="lazy"
                      className={`block h-auto w-full rounded-xl bg-[var(--surface-card)] shadow-[var(--shadow-md)] ring-1 ring-[var(--border-default)] ${feature.images.some((img) => img.height > img.width * 1.5) ? 'max-w-[22rem]' : ''}`}
                    />
                  ))}
                </div>
                <figcaption className="text-left">
                  <h3 className="font-heading text-2xl font-semibold text-[var(--text-primary)]">{feature.title}</h3>
                  <p className="mt-3 text-base leading-7 text-[var(--text-secondary)]">{feature.caption}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {FAMILY_QUOTES.length > 0 && (
        <section aria-labelledby="families-heading" className="px-6 py-8 md:px-12 md:py-12">
          <div className="mx-auto max-w-6xl">
            <h2 id="families-heading" className="font-heading text-3xl font-bold text-[var(--text-primary)]">What families say</h2>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {FAMILY_QUOTES.map((item) => (
                <Card key={item.quote}>
                  <blockquote className="m-0 text-base leading-7 text-[var(--text-primary)]">“{item.quote}”</blockquote>
                  <div className="mt-3 text-sm text-[var(--text-muted)]">{item.attribution}</div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="how-it-works" className="px-6 py-8 md:px-12 md:py-12">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl font-bold text-[var(--text-primary)]">How it works</h2>
            <p className="mt-4 text-base leading-7 text-[var(--text-secondary)]">
              Create your parent account, add your child, then start practice and track progress.
            </p>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {workflowSteps.map((item) => (
              <Card key={item.step}>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-accent-subtle)] text-base font-bold text-[var(--color-accent-hover)]">
                  {item.step}
                </div>
                <h3 className="font-heading mt-4 text-lg font-semibold text-[var(--text-primary)]">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)]">{item.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-8 md:px-12 md:py-12">
        <div className="mx-auto max-w-6xl rounded-2xl bg-[var(--surface-card)] p-8 shadow-[var(--shadow-lg)] ring-1 ring-[var(--border-default)]">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl font-bold text-[var(--text-primary)]">A practical study routine for selective test prep</h2>
            <p className="mt-4 text-base leading-7 text-[var(--text-secondary)]">
              Give your child short practice sessions, targeted revision, and clear parent visibility without making the week feel overloaded.
            </p>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {featureCards.map((card, index) => (
              <div
                key={card.title}
                className={`rounded-lg bg-[var(--bg-subtle)] p-6 ${
                  index === featureCards.length - 1 && featureCards.length % 2 === 1 ? 'md:col-span-2' : ''
                }`}
              >
                <h3 className="font-heading text-xl font-semibold text-[var(--text-primary)]">{card.title}</h3>
                <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)]">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-8 md:px-12 md:py-12">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
            <Card variant="ink" className="p-2">
              <h2 className="font-heading text-3xl font-bold">Who this is for</h2>
              <p className="mt-4 text-base leading-7">
                TestCoachAI was built by a parent preparing their own child for the NSW selective test — aimed at families who want a calmer, more visible preparation routine, not just theory.
              </p>
              <ul className="mt-6 space-y-4 text-sm leading-7">
                {audiencePoints.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Card>
            <div className="grid gap-4 md:grid-cols-2">
              {proofCards.map((card) => (
                <Card key={card.title}>
                  <h3 className="font-heading text-lg font-semibold text-[var(--text-primary)]">{card.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)]">{card.body}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-8 md:px-12 md:py-12">
        <div className="mx-auto max-w-6xl rounded-2xl bg-[var(--surface-card)] p-8 shadow-[var(--shadow-lg)] ring-1 ring-[var(--border-default)]">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl font-bold text-[var(--text-primary)]">How TestCoachAI fits alongside tutoring and workbooks</h2>
            <p className="mt-4 text-base leading-7 text-[var(--text-secondary)]">
              Many families combine TestCoachAI with tutoring or workbooks. Here's what it adds on its own.
            </p>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {differentiators.map((card) => (
              <div key={card.title} className="rounded-lg bg-[var(--bg-subtle)] p-6">
                <h3 className="font-heading text-xl font-semibold text-[var(--text-primary)]">{card.title}</h3>
                <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)]">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-8 md:px-12 md:py-12">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl mb-8">
            <h2 className="font-heading text-3xl font-bold text-[var(--text-primary)]">Available courses</h2>
            <p className="mt-4 text-base leading-7 text-[var(--text-secondary)]">
              Start with a free trial for any course below. After the trial, choose the billing option that suits your family.
            </p>
          </div>
          {availableCourses.length > 0 ? (
            <div className="space-y-6">
              {availableCourses.map((course) => {
                const coursePlans = getCoursePlans(course, staticPlans);
                return (
                  <Card key={course._id} variant="ink" className="p-2">
                    <h3 className="font-heading text-2xl font-bold">{course.name}</h3>
                    {course.description && (
                      <p className="mt-3 text-base leading-7">{course.description}</p>
                    )}
                    <div className="mt-6 grid gap-4 md:grid-cols-2">
                      {coursePlans.map((plan) => {
                        const href = `/register-parent?plan=${encodeURIComponent(plan.key)}&courseId=${encodeURIComponent(course._id)}`;
                        return (
                          <div key={plan.key} className="rounded-lg border-[1px] border-[rgba(255,255,255,0.15)] bg-white/10 p-6 backdrop-blur-sm">
                            <Badge variant="premium">{plan.badge || plan.key}</Badge>
                            <h4 className="font-heading mt-4 text-2xl font-semibold">{plan.name || plan.checkoutName || plan.key}</h4>
                            <p className="mt-2 text-2xl font-bold text-[var(--brass-300)]">{plan.priceLabel}</p>
                            <p className="mt-2 text-sm leading-7">{plan.description}</p>
                            {plan.termSummary && <p className="mt-2 text-sm font-semibold">{plan.termSummary}</p>}
                            {plan.features && (
                              <ul className="mt-4 space-y-3 text-sm leading-6">
                                {plan.features.slice(0, 4).map((feature) => (
                                  <li key={feature}>{feature}</li>
                                ))}
                              </ul>
                            )}
                            <Link
                              to={href}
                              className="tc-btn tc-btn--secondary tc-btn--md mt-6"
                              onClick={() => trackLandingCta('Pricing Plan Click', `${course._id}:${plan.key}`)}
                            >
                              {PRIMARY_CTA_LABEL}
                            </Link>
                            <p className="mt-2 text-xs text-white/70">
                              {plan.autoRenews ? 'No lock-in — cancel anytime from your account.' : 'One payment, no auto-renewal to cancel.'}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card variant="ink" className="p-2">
              <div className="max-w-3xl mb-6">
                <h3 className="font-heading text-2xl font-bold">Choose the billing style that suits your family</h3>
                <p className="mt-3 text-base leading-7">
                  Start with the same free trial, then continue with either flexible monthly access or a single upfront payment for the year.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {staticPlans.map((plan) => {
                  const href = `/register-parent?plan=${encodeURIComponent(plan.key)}`;
                  return (
                    <div key={plan.key} className="rounded-lg border-[1px] border-[rgba(255,255,255,0.15)] bg-white/10 p-6 backdrop-blur-sm">
                      <Badge variant="premium">{plan.badge || plan.key}</Badge>
                      <h4 className="font-heading mt-4 text-2xl font-semibold">{plan.name || plan.checkoutName || plan.key}</h4>
                      <p className="mt-2 text-2xl font-bold text-[var(--brass-300)]">{plan.priceLabel}</p>
                      <p className="mt-2 text-sm leading-7">{plan.description}</p>
                      {plan.termSummary && <p className="mt-2 text-sm font-semibold">{plan.termSummary}</p>}
                      {plan.features && (
                        <ul className="mt-4 space-y-3 text-sm leading-6">
                          {plan.features.slice(0, 4).map((feature) => (
                            <li key={feature}>{feature}</li>
                          ))}
                        </ul>
                      )}
                      <Link
                        to={href}
                        className="tc-btn tc-btn--secondary tc-btn--md mt-6"
                        onClick={() => trackLandingCta('Pricing Plan Click', plan.key)}
                      >
                        {PRIMARY_CTA_LABEL}
                      </Link>
                      <p className="mt-2 text-xs text-white/70">
                        {plan.autoRenews ? 'No lock-in — cancel anytime from your account.' : 'One payment, no auto-renewal to cancel.'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      </section>

      <section id="faqs" className="px-6 py-8 md:px-12 md:py-12">
        <div className="mx-auto max-w-4xl rounded-2xl bg-[var(--surface-card)] p-8 shadow-[var(--shadow-sm)] ring-1 ring-[var(--border-default)]">
          <div className="text-center">
            <h2 className="font-heading text-3xl font-bold text-[var(--text-primary)]">Frequently asked questions</h2>
            <p className="mt-4 text-base leading-7 text-[var(--text-secondary)]">
              Helpful answers for parents considering the free trial.
            </p>
          </div>
          <div className="mt-8 space-y-5">
            {faqEntries.map((item) => (
              <div key={item.question} className="rounded-lg bg-[var(--bg-subtle)] p-5">
                <h3 className="font-heading text-lg font-semibold text-[var(--text-primary)]">{item.question}</h3>
                <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)]">{item.answer}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-base text-[var(--text-secondary)]">
            Still have a question? Email us at{" "}
            <a href={SUPPORT_MAILTO} className="font-semibold text-[var(--color-primary)] underline">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </section>

      <section className="px-6 pb-12 pt-8 md:px-12 md:pb-16">
        <div className="mx-auto max-w-5xl rounded-2xl bg-gradient-to-r from-[var(--ink-700)] to-[var(--sage-600)] p-8 text-center text-white shadow-[var(--shadow-xl)]">
          {testDateCta ? (
            <>
              <h2 className="font-heading text-3xl font-bold">The next NSW selective test is {testDateCta.label}.</h2>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-white/90">
                That&apos;s about {testDateCta.weeks} weeks away, enough time for a few minutes of practice a day to add up.
                {testDateCta.applications ? ` ${testDateCta.applications}` : ''}{' '}
                <a href={testDateCta.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-white underline">
                  NSW Department of Education
                </a>
              </p>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-white/80">{PRICING_COPY}</p>
            </>
          ) : (
            <>
              <h2 className="font-heading text-3xl font-bold">Start with the parent account, then add your child and begin practice.</h2>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-white/90">
                {PRICING_COPY} The first step stays simple: start the free trial, add your child on the next screen, and let practice begin.
              </p>
            </>
          )}
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to={registerHref}
              className="tc-btn tc-btn--secondary tc-btn--lg"
              onClick={() => trackLandingCta('Primary CTA Click', 'Bottom CTA')}
            >
              {PRIMARY_CTA_LABEL}
            </Link>
            <a
              href="#faqs"
              className="inline-flex items-center justify-center rounded-md border border-white/70 px-6 py-3 text-base font-semibold text-white no-underline transition hover:border-white hover:bg-white/10"
              onClick={() => trackLandingCta('Secondary CTA Click', 'Bottom FAQ')}
            >
              Read the FAQs
            </a>
          </div>
        </div>
      </section>

      <footer className="px-6 pb-10 text-center text-sm text-[var(--text-muted)] md:px-12">
        &copy; {new Date().getFullYear()} TestCoachAI. Contact:{" "}
        <a href={SUPPORT_MAILTO} className="text-[var(--text-muted)] underline">{SUPPORT_EMAIL}</a>
        <br />
        Parent-led NSW selective test preparation for Year 4-6 students, designed around steady daily practice.
      </footer>
    </div>
  );
}
